/* eslint-disable @typescript-eslint/naming-convention */
import { memo, ReactElement } from 'react';
import { Field, FieldProps, FieldRenderProps } from 'react-final-form';

import { IReactQuillEditorProps, ReactQuillEditor } from './ReactQuillEditor';
import { IReactQuillDelta } from '../../../APIPackage/KnmModule/interfaces/IReactQuillDelta';

type Value = IReactQuillEditorProps['value'];
type IReactQuillEditorFieldProps = IReactQuillEditorProps &
  FieldProps<Value, FieldRenderProps<Value>> & {
    fieldKey?: ReactElement['key'];
    handleCustomChange?: (value: string) => void;
  } & {
    warning?: string;
  };

export const ReactQuillEditorField = memo((props: IReactQuillEditorFieldProps) => {
  const {
    afterSubmit,
    allowNull,
    beforeSubmit,
    data,
    defaultValue,
    format,
    formatOnBlur,
    initialValue,
    isEqual,
    multiple,
    name,
    parse,
    subscription,
    type,
    validate,
    validateFields,
    value,
    fieldKey,
    handleCustomChange,
    warning,
    ...componentProps
  } = props;

  return (
    <Field
      key={fieldKey} // Required for re-render validation. Cause an error: "Cannot update a component (`ForwardRef(Field)`) while rendering a different component (`ForwardRef(Field)`)." https://github.com/final-form/react-final-form/issues/850
      fieldKey={fieldKey}
      afterSubmit={afterSubmit}
      allowNull={allowNull}
      beforeSubmit={beforeSubmit}
      data={data}
      defaultValue={defaultValue}
      format={format}
      formatOnBlur={formatOnBlur}
      initialValue={initialValue}
      isEqual={isEqual}
      multiple={multiple}
      name={name}
      parse={parse}
      subscription={subscription}
      type={type}
      validate={validate}
      validateFields={validateFields}
      value={value}
    >
      {({ input, meta }) => {
        const { onChange, ...restComponentProps } = componentProps;

        function handleChange(e: IReactQuillDelta) {
          input.onChange(e);
          onChange?.(e);
        }

        const error =
          (meta.touched && restComponentProps.error) || (meta.touched && meta.error)
            ? restComponentProps.error || meta.error
            : undefined;

        return (
          <ReactQuillEditor
            {...restComponentProps}
            {...input}
            warning={warning}
            error={error}
            onChange={handleChange}
            handleCustomChange={handleCustomChange}
          />
        );
      }}
    </Field>
  );
});
